import { useEffect, useRef, useState } from "react";

// Design spec is shared, by hand, with the PDF -- SmartTechAcademyBackend's
// resources/views/certificates/certificate.blade.php carries the identical px values, colors,
// and fonts under matching structure (dompdf can't execute this file, so the coupling can't be
// automatic; that's also why every value below is a literal px number rather than a computed
// style -- so a person porting a design change between the two has an exact, line-by-line
// mapping, not two designs that only *look* similar). Base canvas is 1120x800, exactly matching
// the PDF's page size (CertificateController::downloadPdf()'s ->setPaper([0,0,1120,800])).
const DESIGN_WIDTH = 1120;
const DESIGN_HEIGHT = 800;

// certificate.blade.php formats issuedDate via PHP's `F j, Y` (e.g. "September 21, 2026") --
// the API's issued_at field itself stays a plain ISO date ("2026-09-21", used elsewhere for
// things like sorting the certificates list), so it's formatted the same way here rather than
// changed at the source, matching the PDF's polish without touching that field's other readers.
const formatIssuedDate = (isoDate) => {
  if (!isoDate) return "";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

/**
 * Renders at a fixed 1120x800 design size, then scales the whole subtree down (transform:
 * scale, never up past 1) to fit its container -- the standard technique for a fixed-proportion
 * component that must stay pixel-identical to another fixed-size render (here, the PDF) at any
 * viewport width, rather than reflowing independently the way ordinary responsive CSS would.
 */
const CertificateDocument = ({ certificate }) => {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = outerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setScale(Math.min(width / DESIGN_WIDTH, 1));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const {
    recipient_name: recipientName,
    course_title: courseTitle,
    certificate_number: certificateNumber,
    issued_at: issuedAt,
    president_name: presidentName,
    qr_data_uri: qrDataUri,
    final_score: finalScore,
    certificate_class: certificateClass,
    modules_count: modulesCount,
    lessons_count: lessonsCount,
    quizzes_count: quizzesCount,
  } = certificate;

  // Trims a trailing ".00"/"0" the same way the PDF's rtrim(...) does, e.g. 95 -> "95%",
  // 91.67 -> "91.67%", 90.10 -> "90.1%".
  const formattedScore = finalScore != null ? Number(finalScore).toFixed(2).replace(/\.?0+$/, "") : null;

  return (
    <div ref={outerRef} className="rbt-cert-doc-outer" style={{ height: DESIGN_HEIGHT * scale }}>
      <div className="rbt-cert-doc" style={{ transform: `scale(${scale})` }}>
        <div className="rbt-cert-doc__card">
          <div className="rbt-cert-doc__inner">
            <span className="rbt-cert-doc__crop rbt-cert-doc__crop--tl" aria-hidden="true"></span>
            <span className="rbt-cert-doc__crop rbt-cert-doc__crop--bl" aria-hidden="true"></span>

            <div className="rbt-cert-doc__panel">
              <span className="rbt-cert-doc__seal" aria-hidden="true">
                <span className="rbt-cert-doc__seal-inner">
                  <i className="feather-check"></i>
                </span>
              </span>
              <div className="rbt-cert-doc__eyebrow">
                Course
                <br />
                Certificate
                <small>SmartTech Academy</small>
              </div>
              {certificateClass && (
                <div className={`rbt-cert-doc__class-badge rbt-cert-doc__class-badge--${certificateClass}`}>
                  {certificateClass} class
                </div>
              )}
            </div>
            <span className="rbt-cert-doc__panel-tail" aria-hidden="true"></span>

            <div className="rbt-cert-doc__content">
              <span className="rbt-cert-doc__texture" aria-hidden="true"></span>
              <span className="rbt-cert-doc__texture rbt-cert-doc__texture--inner" aria-hidden="true"></span>

              {/* eslint-disable-next-line @next/next/no-img-element -- see .rbt-cert-doc__mark comment above */}
              <img className="rbt-cert-doc__logo" src="/images/logo/smarttech-full-blue.png" alt="SmartTech Academy" />

              <div className="rbt-cert-doc__chip">
                Cert. No. <b>{certificateNumber}</b>
              </div>

              <div className="rbt-cert-doc__subtitle">This certifies that</div>
              <div className="rbt-cert-doc__recipient">{recipientName}</div>

            <div className="rbt-cert-doc__divider" />

            <div className="rbt-cert-doc__bodytext">has successfully completed all requirements for the course</div>
            <div className="rbt-cert-doc__coursetitle">{courseTitle}</div>
            <div className="rbt-cert-doc__awarded">Awarded on {formatIssuedDate(issuedAt)}</div>

            {formattedScore != null && (
              <>
                <div className="rbt-cert-doc__score-value">{formattedScore}%</div>
                <div className="rbt-cert-doc__score-label">Final Course Score</div>
                <div className="rbt-cert-doc__stats">
                  <div className="rbt-cert-doc__stat">
                    <div className="rbt-cert-doc__stat-value">{modulesCount ?? 0}</div>
                    <div className="rbt-cert-doc__stat-label">Modules</div>
                  </div>
                  <div className="rbt-cert-doc__stat">
                    <div className="rbt-cert-doc__stat-value">{lessonsCount ?? 0}</div>
                    <div className="rbt-cert-doc__stat-label">Lessons</div>
                  </div>
                  <div className="rbt-cert-doc__stat">
                    <div className="rbt-cert-doc__stat-value">{quizzesCount ?? 0}</div>
                    <div className="rbt-cert-doc__stat-label">Quizzes</div>
                  </div>
                </div>
              </>
            )}

            <div className="rbt-cert-doc__footer">
              <div className="rbt-cert-doc__footer-col">
                {/* Two swatches, toggled by CSS (.active-gray-mode/.active-dark-mode), the same
                    dark-mode pattern this file already uses for text colors -- a scanned/photographed
                    signature is baked-in navy ink, so it needs its own light/dark swap instead of a
                    CSS color property. eslint-disable: see the logo <img> above, same scaled-subtree
                    reasoning. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="rbt-cert-doc__signature-img rbt-cert-doc__signature-img--light"
                  src="/images/certificate/signature-navy.png"
                  alt={`${presidentName} signature`}
                />
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="rbt-cert-doc__signature-img rbt-cert-doc__signature-img--dark"
                  src="/images/certificate/signature-white.png"
                  alt={`${presidentName} signature`}
                />
                <div className="rbt-cert-doc__signature-line" />
                <div className="rbt-cert-doc__signature-title">President</div>
              </div>
              <div className="rbt-cert-doc__footer-col">
                {qrDataUri && (
                  // QR is a generated data: URI (not an optimizable remote asset) inside the scaled subtree.
                  // eslint-disable-next-line @next/next/no-img-element
                  <img className="rbt-cert-doc__qr" src={qrDataUri} alt="Scan to verify this certificate" />
                )}
                <div className="rbt-cert-doc__qr-caption">Scan to verify</div>
              </div>
              <div className="rbt-cert-doc__footer-col">
                <div className="rbt-cert-doc__site-url">www.smarttechacademy.org</div>
                <div className="rbt-cert-doc__site-note">
                  Verify this certificate online
                  <br />
                  using the certificate number above
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateDocument;
